import React, { useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

type BedroomMarketRow = {
  bedrooms?: number | string;
  median?: number | null;
  average?: number | null;
  medianPerSqFt?: number | null;
  averagePerSqFt?: number | null;
  medianDaysOnMarket?: number | null;
  averageDaysOnMarket?: number | null;
  totalListings?: number | null;
};

type Metric = 'rent' | 'sqft' | 'dom' | 'listings';

const money = (value: number) => `$${Math.round(value).toLocaleString()}`;
const decimalMoney = (value: number) => `$${value.toFixed(2)}`;

const RentalBedroomAnalysisCard: React.FC<{ rows?: BedroomMarketRow[] }> = ({ rows }) => {
  const [metric, setMetric] = useState<Metric>('rent');

  const data = useMemo(
    () =>
      (rows || [])
        .filter((row) => row.bedrooms != null && row.bedrooms !== '')
        .map((row) => ({
          label: `${row.bedrooms} ${Number(row.bedrooms) === 1 ? 'Bed' : 'Beds'}`,
          bedrooms: row.bedrooms,
          median: Number(row.median) || 0,
          average: Number(row.average) || 0,
          medianPerSqFt: Number(row.medianPerSqFt ?? row.averagePerSqFt) || 0,
          averagePerSqFt: Number(row.averagePerSqFt ?? row.medianPerSqFt) || 0,
          medianDom: Number(row.medianDaysOnMarket ?? row.averageDaysOnMarket) || 0,
          averageDom: Number(row.averageDaysOnMarket ?? row.medianDaysOnMarket) || 0,
          listings: Number(row.totalListings) || 0,
        }))
        .sort((a, b) => Number(a.bedrooms) - Number(b.bedrooms)),
    [rows],
  );

  if (!data.length) return null;

  const metricConfig = {
    rent: {
      title: 'Median vs Average Rent by Bedrooms',
      subtitle: 'Monthly Rent (USD)',
      medianKey: 'median',
      averageKey: 'average',
      medianLabel: 'Median Rent',
      averageLabel: 'Average Rent',
      tick: (value: number) => money(value),
      value: (value: number) => money(value),
    },
    sqft: {
      title: 'Median vs Average Rent per Sq Ft',
      subtitle: 'Monthly Rent per Square Foot',
      medianKey: 'medianPerSqFt',
      averageKey: 'averagePerSqFt',
      medianLabel: 'Median $/Sq Ft',
      averageLabel: 'Average $/Sq Ft',
      tick: (value: number) => decimalMoney(value),
      value: (value: number) => decimalMoney(value),
    },
    dom: {
      title: 'Median vs Average Days on Market',
      subtitle: 'Days on Market',
      medianKey: 'medianDom',
      averageKey: 'averageDom',
      medianLabel: 'Median DOM',
      averageLabel: 'Average DOM',
      tick: (value: number) => `${value}`,
      value: (value: number) => `${Math.round(value)} days`,
    },
    listings: {
      title: 'Active Listings by Bedrooms',
      subtitle: 'Active Listings',
      medianKey: 'listings',
      averageKey: 'listings',
      medianLabel: 'Listings',
      averageLabel: '',
      tick: (value: number) => `${value}`,
      value: (value: number) => `${Math.round(value)}`,
    },
  }[metric];

  const insight = [...data]
    .filter((row) => row.listings > 0)
    .sort((a, b) => {
      const aScore = a.median / Math.max(a.medianDom, 1) + a.listings * 8;
      const bScore = b.median / Math.max(b.medianDom, 1) + b.listings * 8;
      return bScore - aScore;
    })[0];

  return (
    <div
      style={{
        border: '1px solid #e8eef3',
        borderRadius: 14,
        background: '#fff',
        boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
        padding: 16,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: '#12858d',
            fontSize: 18,
            fontWeight: 800,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#dff4f5',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
            }}
          >
            ↗
          </span>
          Rental Data by Bedrooms
        </div>
        <div
          style={{
            display: 'flex',
            border: '1px solid #dce3e8',
            borderRadius: 8,
            overflow: 'hidden',
            height: 34,
          }}
        >
          {([
            ['rent', 'Rent'],
            ['sqft', '$/Sq Ft'],
            ['dom', 'DOM'],
            ['listings', 'Listings'],
          ] as Array<[Metric, string]>).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setMetric(key)}
              style={{
                border: 0,
                borderRight: key === 'listings' ? 0 : '1px solid #edf0f2',
                padding: '0 16px',
                background: metric === key ? '#1098a0' : '#fff',
                color: metric === key ? '#fff' : '#334155',
                fontSize: 12,
                fontWeight: metric === key ? 700 : 500,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(180px, 220px)',
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              marginBottom: 8,
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#17233d' }}>{metricConfig.title}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{metricConfig.subtitle}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 18, fontSize: 11, color: '#475569', margin: '8px 0' }}>
            <span><span style={{ color: '#12366b', fontSize: 17 }}>■</span> {metricConfig.medianLabel}</span>
            {metricConfig.averageLabel && (
              <span><span style={{ color: '#12a3aa', fontSize: 17 }}>●</span> {metricConfig.averageLabel}</span>
            )}
          </div>

          <div style={{ width: '100%', height: 290 }}>
            <ResponsiveContainer>
              <ComposedChart data={data} margin={{ top: 24, right: 24, left: 8, bottom: 10 }}>
                <CartesianGrid stroke="#e7edf2" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} axisLine={{ stroke: '#cbd5e1' }} />
                <YAxis
                  tickFormatter={metricConfig.tick}
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={62}
                />
                <Tooltip formatter={(value: number) => metricConfig.value(Number(value))} />
                <Bar dataKey={metricConfig.medianKey} fill="#12366b" radius={[3, 3, 0, 0]} maxBarSize={48}>
                  <LabelList
                    dataKey={metricConfig.medianKey}
                    position="top"
                    formatter={(value: number) => metricConfig.value(Number(value))}
                    style={{ fill: '#17233d', fontSize: 10, fontWeight: 700 }}
                  />
                </Bar>
                {metric !== 'listings' && (
                  <Line
                    dataKey={metricConfig.averageKey}
                    type="monotone"
                    stroke="#12a3aa"
                    strokeWidth={2}
                    dot={{ r: 4, fill: '#12a3aa', stroke: '#fff', strokeWidth: 1 }}
                  >
                    <LabelList
                      dataKey={metricConfig.averageKey}
                      position="top"
                      offset={10}
                      formatter={(value: number) => metricConfig.value(Number(value))}
                      style={{ fill: '#1098a0', fontSize: 10, fontWeight: 700 }}
                    />
                  </Line>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 24,
              overflowX: 'auto',
              paddingBottom: 6,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {data.map((row) => (
              <div
                key={row.label}
                style={{
                  border: '1px solid #e5eaf0',
                  borderRadius: 8,
                  padding: 7,
                  textAlign: 'center',
                  minWidth: 118,
                  flex: '0 0 118px',
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 700, color: '#17233d', marginBottom: 5 }}>{row.label}</div>
                {row.listings <= 1 && (
                  <div
                    style={{
                      background: '#fdebea',
                      color: '#be3428',
                      borderRadius: 5,
                      padding: '3px 2px',
                      fontSize: 9,
                      lineHeight: 1.25,
                      marginBottom: 5,
                    }}
                  >
                    ! Low confidence:<br />only {row.listings} listing
                  </div>
                )}
                <div style={{ background: '#eaf8f8', color: '#167c83', borderRadius: 5, padding: '3px 2px', fontSize: 10 }}>
                  ◇ {row.medianPerSqFt ? `${decimalMoney(row.medianPerSqFt)} / sq ft` : '— / sq ft'}
                </div>
                <div style={{ background: '#edf4ff', color: '#3569a7', borderRadius: 5, padding: '3px 2px', fontSize: 10, marginTop: 4 }}>
                  ◷ {row.medianDom ? `${Math.round(row.medianDom)} days (DOM)` : '— DOM'}
                </div>
                <div style={{ background: '#eaf8f4', color: '#15806d', borderRadius: 5, padding: '3px 2px', fontSize: 10, marginTop: 4 }}>
                  ▤ {row.listings} {row.listings === 1 ? 'listing' : 'listings'}
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside
          style={{
            borderRadius: 12,
            background: 'linear-gradient(180deg, #eaf8f9 0%, #f5fbfc 100%)',
            color: '#17233d',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: 18,
          }}
        >
          <div>
            <div
              style={{
                width: 58,
                height: 58,
                borderRadius: '50%',
                border: '2px solid #fff',
                background: '#dff4f5',
                color: '#1098a0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px',
                fontSize: 28,
              }}
            >
              ↗
            </div>
            <div style={{ color: '#12858d', fontSize: 17, fontWeight: 800 }}>Key Insight</div>
            <div style={{ width: 36, height: 3, borderRadius: 2, background: '#12a3aa', margin: '10px auto 14px' }} />
            {insight ? (
              <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.55 }}>
                {insight.label} rentals stand out with {money(insight.median)} median rent,
                {' '}{Math.round(insight.medianDom)} DOM, and {insight.listings} active listings.
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#64748b' }}>Not enough rental listing data for an insight.</div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default RentalBedroomAnalysisCard;
